/**
 * Authentication context and hook
 * Manages user session and authentication state
 */

import { AuthClient } from "@eoln/sdk-auth";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { clearSession, getSession, hasSession, setSession } from "@/lib/session";

const AUTH_GRAPHQL_ENDPOINT = import.meta.env.VITE_AUTH_GRAPHQL_ENDPOINT || "https://auth-api.mono-dev.eoln.eu/graphql";

interface AuthContextValue {
  isAuthenticated: boolean;
  apiKey: string | null;
  login: (apiKey: string) => Promise<void>;
  logout: () => void;
  client: AuthClient | null;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(hasSession());
  const [apiKey, setApiKey] = useState<string | null>(getSession());

  const client = useMemo(
    () =>
      apiKey
        ? new AuthClient({
            endpoint: AUTH_GRAPHQL_ENDPOINT,
            apiKey,
          })
        : null,
    [apiKey],
  );

  const login = useCallback(async (newApiKey: string) => {
    // Validate API key format: sk_ + 64 hex characters
    const apiKeyPattern = /^sk_[0-9a-f]{64}$/;
    if (!apiKeyPattern.test(newApiKey)) {
      throw new Error("Invalid API key format. Expected: sk_ followed by 64 hex characters");
    }

    // Validate key with backend
    const tempClient = new AuthClient({
      endpoint: AUTH_GRAPHQL_ENDPOINT,
      apiKey: newApiKey,
    });

    const authInfo = await tempClient.getAuthInfo();
    if (!authInfo.isAuthenticated) {
      throw new Error("API key is not valid or has been revoked");
    }

    // Store in session
    setSession(newApiKey);
    setApiKey(newApiKey);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setApiKey(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, apiKey, login, logout, client }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
